import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Button — pill-shape primary CTA primitive.
 *
 * Sketched in Claude Design 2026-05-10 (dashboard); extended for login 2026-05-19.
 * Rationale: lg size (48 px) for tactile primary actions on hero
 * surfaces; md size (40 px) for inline/dense layouts. Primary variant
 * uses --brand-500 + white text + --shadow-sm; secondary uses
 * --bg-surface + --border-default + --fg-primary.
 */
import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { Icon } from './Icon.js';
import { cn } from './cn.js';
// SCRUM-81 audit exemption (`md.px-[18px]`): the md-size handoff
// padding is 18 px on the x-axis (no Tailwind default — `4`=16 px,
// `5`=20 px straddle it). Awaiting a branded `spacing[4.5]` token or
// an SCRUM-79 inline-disable + citation. The `text-[13px]` on the sm
// size and the `text-sm`/`text-base` on md/lg are typography
// arbitrary values outside SCRUM-79's scope (it targets
// sizing/spacing/positioning).
//
// Note: this file lives in `packages/ui-kit/src/`, which is outside
// the `@sparta/no-pixel-literal-arbitrary` rule's scope (the rule
// scopes to `apps/admin/src/{widgets,modules,pages}/**`). The kit is
// the canonical home for these brand-deliberate values; the rule
// targets app code where ad-hoc pixel literals are doctrine drift.
const SIZE_CLASSES = {
    sm: 'py-1.5 px-3.5 text-[13px] h-8',
    md: 'py-2.5 px-[18px] text-sm h-10',
    lg: 'py-3.5 px-6 text-base h-12',
};
const SIZE_ICON_PX = {
    sm: 14,
    md: 16,
    lg: 16,
};
const VARIANT_CLASSES = {
    primary: 'bg-[var(--bg-brand)] text-[var(--fg-on-brand)] border-transparent',
    secondary: 'bg-[var(--bg-surface)] text-[var(--fg-primary)] border-[var(--border-default)]',
    ghost: 'bg-transparent text-[var(--fg-primary)] border-transparent',
    danger: 'bg-[var(--fg-danger)] text-white border-transparent',
    quiet: 'bg-[var(--bg-sunken)] text-[var(--fg-primary)] border-transparent',
};
// Base classes apply to every variant. Focus-visible ring + disabled
// behavior are the a11y baseline (CLAUDE.md FE rule §7).
const BASE_CLASSES = [
    'inline-flex',
    'items-center',
    'justify-center',
    'gap-2',
    'font-semibold',
    'rounded-full',
    'cursor-pointer',
    'border border-solid',
    'whitespace-nowrap',
    'transition-[background,color,box-shadow,transform]',
    'duration-[var(--dur-fast)]',
    'focus-visible:outline-none',
    'focus-visible:ring-2',
    'focus-visible:ring-brand-500',
    'focus-visible:ring-offset-2',
    'disabled:pointer-events-none',
    'disabled:opacity-50',
].join(' ');
export const Button = React.forwardRef(function Button({ variant = 'primary', size = 'md', icon, iconRight, full = false, asChild = false, children, className, type, ...rest }, ref) {
    const widthClass = full ? 'w-full' : 'w-auto';
    const composed = cn(BASE_CLASSES, SIZE_CLASSES[size], VARIANT_CLASSES[variant], widthClass, className);
    const iconSize = SIZE_ICON_PX[size];
    // Radix Slot path: merge classes onto the child, do NOT render a
    // <button>. Type attribute does not apply (children are anchors,
    // labels, etc.).
    if (asChild) {
        return (_jsx(Slot, { ref: ref, className: composed, ...rest, children: children }));
    }
    // Default <button> path: type defaults to "button" so an unwitting
    // <Button> inside a <form> does NOT submit by accident. Consumers
    // opt in to submit by passing `type="submit"`.
    return (_jsxs("button", { ref: ref, type: type ?? 'button', className: composed, ...rest, children: [icon !== undefined ? _jsx(Icon, { name: icon, size: iconSize }) : null, children, iconRight !== undefined ? _jsx(Icon, { name: iconRight, size: iconSize }) : null] }));
});
Button.displayName = 'Button';
//# sourceMappingURL=Button.js.map